import Header from './Header';
import background from './img/softwarePartners.webp';
import screenshotDeveloperHub from './img/screenshot-developer-hub.webp';
import screenshotCreateNewApp from './img/screenshot-create-new-application.webp';
import screenshotSecrets from './img/screenshot-secrets.webp';
import screenshotWebhooks from './img/screenshot-webhooks.webp';
import screenshotModuleFolder from './img/screenshot-adobe-magento-module-folder.png';
import screenshotPaymentMethods from './img/screenshot-adobe-magento-payment-methods.png';
import screenshotSettings from './img/screenshot-adobe-magento-settings.png';
import screenshotWebhookUrl from './img/screenshot-adobe-magento-webhook-url.png';
import screenshotCheckout from './img/screenshot-adobe-magento-checkout.png';
import screenshotLogs from './img/screenshot-adobe-magento-logs.png';
import MobileNavigation from './MobileNavigation';
import { CopyBlock, dracula } from 'react-code-blocks';
import Footer from './Footer';
import { Link } from 'react-router-dom';
import { Section, openSection } from './DocSection';

const wrap = { textWrapMode: 'wrap', textWrapStyle: 'auto', width: '100%' };
const linkStyle = { color: '#007bff', textDecoration: 'underline' };

const DeveloperAdobeMagento = () => {
  return (
    <>
      <MobileNavigation />
      <section className="bg-[#212121] woo-commerce">
        <div
          className="w-full pb-28"
          style={{
            backgroundImage: `url(${background})`,
            backgroundSize: '200% 200%',
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Header />
          <div className="p-4 row md:p-8 how">
            <h3 className="text-white large">iQ Pro+ Adobe Commerce Plugin</h3>
          </div>
          <div className="flex p-16 row flex-col justify-center flex-start gap-6 self-stretch rounded-[28px] bg-white !mb-10">
            <h2 className="text-black large">Overview</h2>
            <p className="text-black light" style={wrap}>
              The{' '}
              <Link to="/integrations-adobe-magento-downloads" style={linkStyle}>
                iQ Pro+ payment gateway plugin
              </Link>{' '}
              connects your Adobe Commerce or Magento Open Source store to your iQ Pro+ Gateway. It
              lets you take credit card payments at checkout with secure, PCI-compliant
              tokenization. Your shopper types their card number into a payment window hosted by the
              gateway, so full card data never touches your store or your server. Your store
              receives only a token, a safe stand-in for the card number.
            </p>
            <p className="text-black light" style={wrap}>
              This guide covers installing the plugin, connecting it to your gateway, setting up
              webhooks, testing your credentials, and managing orders after you take a payment.
            </p>
            <div className="my-4 callout">
              <p className="mb-0 text-black light" style={wrap}>
                <strong>NOTE:</strong> Adobe Commerce and Magento Open Source are the same platform
                under the hood. Adobe Commerce is the paid version. Everything in this guide applies
                to both, and the admin screens look the same in each.
              </p>
            </div>
            <h2 className="font-normal text-black">On this page</h2>
            <ol className="ml-5 list-decimal gap-x-12 marker:text-gray-500 md:columns-2 [&>li]:break-inside-avoid [&>li]:py-1">
              <li className="text-black light">
                <a onClick={openSection} href="#installation-and-setup" style={linkStyle}>
                  Installation and Setup
                </a>
              </li>
              <li className="text-black light">
                <a onClick={openSection} href="#app-registration-setup" style={linkStyle}>
                  App Registration Setup
                </a>
              </li>
              <li className="text-black light">
                <a onClick={openSection} href="#settings-page" style={linkStyle}>
                  Settings Page
                </a>
              </li>
              <li className="text-black light">
                <a onClick={openSection} href="#credentials-and-environment" style={linkStyle}>
                  Credentials and Environment
                </a>
              </li>
              <li className="text-black light">
                <a onClick={openSection} href="#webhook-setup" style={linkStyle}>
                  Webhook Setup
                </a>
              </li>
              <li className="text-black light">
                <a onClick={openSection} href="#webhook-creation" style={linkStyle}>
                  Webhook Creation
                </a>
              </li>
              <li className="text-black light">
                <a onClick={openSection} href="#turning-on-the-payment-method" style={linkStyle}>
                  Turning On the Payment Method
                </a>
              </li>
              <li className="text-black light">
                <a onClick={openSection} href="#managing-orders-after-payment" style={linkStyle}>
                  Managing Orders After Payment
                </a>
              </li>
              <li className="text-black light">
                <a onClick={openSection} href="#logging-and-troubleshooting" style={linkStyle}>
                  Logging and Troubleshooting
                </a>
              </li>
              <li className="text-black light">
                <a onClick={openSection} href="#styling" style={linkStyle}>
                  Styling
                </a>
              </li>
              <li className="text-black light">
                <a onClick={openSection} href="#support" style={linkStyle}>
                  Support
                </a>
              </li>
            </ol>
            <h2 className="font-normal text-black">Prerequisites</h2>
            <p className="text-black light">This plugin is compatible with the following:</p>
            <table className="tg" style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '33%' }} />
                <col style={{ width: '34%' }} />
                <col style={{ width: '33%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="font-bold text-center text-black">Component</th>
                  <th className="font-bold text-center text-black">Compatible Versions</th>
                  <th className="font-bold text-center text-black">Recommended</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <b>Adobe Commerce / Magento Open Source</b>
                  </td>
                  <td>2.4.x</td>
                  <td>
                    <b>2.4.8</b>
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>PHP</b>
                  </td>
                  <td>8.1.x, 8.2.x, 8.3.x</td>
                  <td>
                    <b>8.3.x</b>
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>Currency</b>
                  </td>
                  <td>US Dollars (USD) only</td>
                  <td>
                    <b>USD</b>
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>Payment types</b>
                  </td>
                  <td>Credit and debit cards</td>
                  <td>
                    <b>Credit and debit cards</b>
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="text-black light">You will also need:</p>
            <ul className="ml-5 list-disc">
              <li className="text-black light" style={wrap}>
                An active iQ Pro+ gateway account, with login access to the <b>gateway portal</b>.
              </li>
              <li className="text-black light">
                Administrator access to your store’s Magento Admin Panel.
              </li>
              <li className="text-black light" style={wrap}>
                Server access, or help from your developer or hosting provider. Installing the
                plugin means copying files onto the server and running a few commands, not clicking
                an upload button in the admin.
              </li>
              <li className="text-black light" style={wrap}>
                A valid SSL certificate on your live site, so your store loads over{' '}
                <code>https://</code>. The secure payment window and webhooks both require it.
              </li>
              <li className="text-black light" style={wrap}>
                Magento cron running on your server. The plugin uses cron to apply payment updates
                sent by the gateway. Most stores already have this set up.
              </li>
            </ul>
            <Section id="installation-and-setup" title="Installation and Setup" open>
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>NOTE:</strong> If you install a new version of the plugin later, clear
                  your store cache afterward. A stale cache is the most common cause of odd behavior
                  right after an update.
                </p>
              </div>
              <h2 className="font-normal text-black">Uploading the Plugin</h2>
              <ol className="ml-5 list-decimal">
                <li className="text-black light" style={wrap}>
                  Download the{' '}
                  <Link to="/integrations-adobe-magento-downloads" style={linkStyle}>
                    plugin <code>.zip</code> file
                  </Link>{' '}
                  and unzip it on your computer.
                </li>
                <li className="text-black light" style={wrap}>
                  Connect to your store’s server using SFTP, SSH, or your hosting control panel’s
                  file manager.
                </li>
                <li className="text-black light" style={wrap}>
                  Copy the unzipped files into your Magento installation so that they land in this
                  exact folder:
                  <CopyBlock
                    text="app/code/Basys/IQProPayment/"
                    theme={dracula}
                    customStyle={{ overflowX: 'auto' }}
                    language="text"
                  />
                </li>
              </ol>
              <p className="text-black light" style={wrap}>
                When you are done, the folder should contain files such as{' '}
                <code>registration.php</code>, <code>composer.json</code>, and folders named{' '}
                <code>etc</code>, <code>Model</code>, and <code>view</code>.
              </p>
              <img src={screenshotModuleFolder} alt="Screenshot of the installed plugin folder" />
              <h2 className="font-normal text-black">Turning the Plugin On</h2>
              <p className="text-black light" style={wrap}>
                Open a terminal on the server and go to your Magento root folder (the folder that
                contains the <code>bin</code> directory). Then run these commands one at a time, in
                this order:
              </p>
              <CopyBlock
                text={`bin/magento module:enable Basys_IQProPayment
bin/magento setup:upgrade
bin/magento setup:di:compile
bin/magento setup:static-content:deploy -f en_US
bin/magento cache:flush`}
                theme={dracula}
                customStyle={{ overflowX: 'auto' }}
                language="bash"
              />
              <p className="text-black light">What each command does:</p>
              <ul className="ml-5 list-disc">
                <li className="text-black light">
                  <code>module:enable</code> turns the plugin on.
                </li>
                <li className="text-black light" style={wrap}>
                  <code>setup:upgrade</code> installs the plugin’s database tables and settings.
                </li>
                <li className="text-black light" style={wrap}>
                  <code>setup:di:compile</code> and <code>setup:static-content:deploy</code> rebuild
                  the files your storefront needs. These two are required if your store runs in
                  production mode. They are harmless to run either way.
                </li>
                <li className="text-black light">
                  <code>cache:flush</code> clears the cache so your changes show up.
                </li>
              </ul>
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>IMPORTANT:</strong> Run these commands as the same user that owns your
                  Magento files, not as root. Running them as root can create files your web server
                  cannot read, which takes the site down.
                </p>
              </div>
              <h2 className="font-normal text-black">Confirming the Plugin Is Installed</h2>
              <p className="text-black light">Run this command:</p>
              <CopyBlock
                text="bin/magento module:status Basys_IQProPayment"
                theme={dracula}
                customStyle={{ overflowX: 'auto' }}
                language="bash"
              />
              <p className="text-black light" style={wrap}>
                You should see the module listed as <b>Enabled</b>. You can also check in the
                Magento Admin Panel: go to <b>Stores → Configuration → Sales → Payment Methods</b>.
                A section named <b>iQPro+ Payment Gateway</b> will now appear in the list.
              </p>
              <img
                src={screenshotPaymentMethods}
                alt="Screenshot of Magento Payment Methods showing iQPro+ Payment Gateway"
              />
            </Section>
            <Section id="app-registration-setup" title="App Registration Setup">
              <p className="text-black light" style={wrap}>
                Next, create an application in your gateway. This gives you the Client ID and Client
                Secret the plugin needs to talk to the gateway. Log in to your gateway portal and go
                to <b>Developer Hub → Applications</b>.
              </p>
              <img src={screenshotDeveloperHub} alt="Screenshot of Developer Hub" />
              <h2 className="font-normal text-black">Creating the Client Credentials</h2>
              <ol className="ml-5 list-decimal">
                <li className="text-black light">
                  On the <b>Applications</b> page, select <b>Create New +</b>.
                </li>
                <li className="text-black light" style={wrap}>
                  Enter your <b>Application Name</b>. For the role, select{' '}
                  <b>Integrator Service Account</b>, then click <b>Create</b>.
                  <div className="my-4 callout">
                    <p className="mb-0 text-black light">
                      <strong>IMPORTANT:</strong> The role <span className="underline">MUST</span>{' '}
                      be Integrator Service Account. If you pick any other role, the plugin will not
                      work.
                    </p>
                  </div>
                  <img src={screenshotCreateNewApp} alt="Screenshot of Create New Application" />
                </li>
                <li className="text-black light" style={wrap}>
                  After you create the application, you will see <b>Client ID</b> listed under the
                  application name, followed by a long string of letters and numbers. That is your
                  Client ID.
                </li>
                <li className="text-black light" style={wrap}>
                  Below <b>Details</b> you will see <b>Secrets</b>. Select <b>+ Add New Secret</b>{' '}
                  and give the secret a name.
                  <img src={screenshotSecrets} alt="Screenshot of Secrets" />
                  <div className="my-4 callout">
                    <p className="mb-0 text-black light" style={wrap}>
                      <strong>IMPORTANT:</strong> The gateway shows the secret once, right after you
                      create it. Copy it and store it somewhere safe before you leave the page. You
                      get two secrets per application, no more.
                    </p>
                  </div>
                </li>
                <li className="text-black light" style={wrap}>
                  You now have your Client ID and Client Secret. Keep them handy for the next
                  section.
                </li>
              </ol>
              <h2 className="font-normal text-black">Your Gateway ID and Processor ID</h2>
              <p className="text-black light" style={wrap}>
                Along with the Client ID and Client Secret, the plugin needs your <b>Gateway ID</b>.
                It identifies the gateway your account uses. If you do not know yours, contact your
                Basys representative.
              </p>
              <p className="text-black light" style={wrap}>
                The <b>Processor ID</b> field is optional. Leave it blank and the gateway will route
                payments through the default processor set up on your account. Only fill this in if
                Basys tells you to send payments through a specific processor.
              </p>
            </Section>
            <Section id="settings-page" title="Settings Page">
              <p className="text-black light" style={wrap}>
                All plugin settings live in one place in the Magento Admin Panel. Navigate to:
              </p>
              <p className="font-semibold text-black" style={wrap}>
                Stores → Configuration → Sales → Payment Methods → iQPro+ Payment Gateway
              </p>
              <img src={screenshotSettings} alt="Screenshot of iQPro+ Payment Gateway settings" />
              <p className="text-black light">From here you can:</p>
              <ul className="ml-5 list-disc">
                <li className="text-black light">
                  Turn the payment method <b>on or off</b> at checkout.
                </li>
                <li className="text-black light" style={wrap}>
                  Choose which <b>environment</b> the plugin runs in (Sandbox or Production).
                </li>
                <li className="text-black light" style={wrap}>
                  Enter the <b>Client ID</b>, <b>Client Secret</b>, and <b>Gateway ID</b> from your
                  gateway.
                </li>
                <li className="text-black light">
                  Copy your <b>Webhook URL</b>.
                </li>
                <li className="text-black light" style={wrap}>
                  Turn on <b>Advanced Logging</b> and open the <b>Logs</b> viewer for
                  troubleshooting.
                </li>
                <li className="text-black light">
                  Run <b>connection tests</b> to confirm your credentials work.
                </li>
              </ul>
              <h2 className="font-normal text-black">Every Setting Explained</h2>
              <table className="tg" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '35%' }} />
                  <col style={{ width: '65%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="font-bold text-center text-black">Setting</th>
                    <th className="font-bold text-center text-black">What it does</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <b>Enabled</b>
                    </td>
                    <td>
                      Yes or No. Shows or hides iQ Pro+ as a payment choice at checkout. Leave this
                      set to No until you have finished testing.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Title</b>
                    </td>
                    <td>
                      The name your shoppers see at checkout. The default is “iQPro+ Payment
                      Gateway”. You can change it to anything, for example “Credit Card”.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Environment</b>
                    </td>
                    <td>
                      Sandbox for testing, or Production for real payments. Only one is active at a
                      time.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Sandbox Client ID</b>
                    </td>
                    <td>The Client ID from your sandbox application.</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Sandbox Client Secret</b>
                    </td>
                    <td>
                      The secret from your sandbox application. Magento stores this encrypted and
                      shows it back as dots.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Sandbox Gateway ID</b>
                    </td>
                    <td>Your sandbox gateway identifier. Required.</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Sandbox Processor ID</b>
                    </td>
                    <td>Optional. Leave blank to use the gateway default processor.</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Production Client ID</b>
                    </td>
                    <td>The Client ID from your live application.</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Production Client Secret</b>
                    </td>
                    <td>The secret from your live application. Stored encrypted.</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Production Gateway ID</b>
                    </td>
                    <td>Your live gateway identifier. Required before going live.</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Production Processor ID</b>
                    </td>
                    <td>Optional. Leave blank to use the gateway default processor.</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Sort Order</b>
                    </td>
                    <td>
                      Where iQ Pro+ appears in the list of payment options at checkout. A lower
                      number moves it higher up the list.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Webhook URL</b>
                    </td>
                    <td>
                      Read-only. This is the address the gateway sends payment updates to. Use the
                      Copy button next to it.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Advanced Logging</b>
                    </td>
                    <td>
                      Yes or No. When Yes, the plugin records every step of each payment in detail.
                      Turn this on while testing or when troubleshooting.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Log Retention (Days)</b>
                    </td>
                    <td>
                      How many days of log files to keep. The plugin deletes older files once a day.
                      The default is 30.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Plugin Logs</b>
                    </td>
                    <td>A View Logs button that opens the plugin’s built-in log viewer.</td>
                  </tr>
                </tbody>
              </table>
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>NOTE:</strong> Remember to click <b>Save Config</b> in the top right after
                  changing anything. Settings do not take effect until you save.
                </p>
              </div>
            </Section>
            <Section id="credentials-and-environment" title="Credentials and Environment">
              <h2 className="font-normal text-black">Selecting an Environment</h2>
              <p className="text-black light" style={wrap}>
                At the top of the settings section, choose which environment the plugin should run
                in:
              </p>
              <ul className="ml-5 list-disc">
                <li className="text-black light">
                  <b>Sandbox</b> - used for testing. No real money moves.
                </li>
                <li className="text-black light">
                  <b>Production</b> - used for live transactions with real money.
                </li>
              </ul>
              <p className="text-black light" style={wrap}>
                Only one environment can be active at a time. The environment you pick decides which
                set of credentials the plugin uses.
              </p>
              <h2 className="font-normal text-black">Entering API Credentials</h2>
              <p className="text-black light" style={wrap}>
                The settings page has two sets of credential fields, one for Sandbox and one for
                Production. You can fill in both and switch between them, or fill in just the one
                you are using. For the environment you selected, enter:
              </p>
              <ul className="ml-5 list-disc">
                <li className="text-black light" style={wrap}>
                  <b>Client ID</b> - the unique identifier for your registered application.
                </li>
                <li className="text-black light" style={wrap}>
                  <b>Client Secret</b> - the secret key that proves the request came from you.
                </li>
                <li className="text-black light" style={wrap}>
                  <b>Gateway ID</b> - the identifier for the gateway assigned to your account.
                </li>
                <li className="text-black light">
                  <b>Processor ID</b> - optional. Leave blank unless Basys gives you one.
                </li>
              </ul>
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>IMPORTANT:</strong> Credentials must match the selected environment.
                  Sandbox credentials will not work in Production mode, and Production credentials
                  will not work in Sandbox mode.
                </p>
              </div>
              <p className="text-black light" style={wrap}>
                After entering your values, click <b>Save Config</b> at the top right of the page.
              </p>
              <h2 className="font-normal text-black">Testing the Connection</h2>
              <p className="text-black light" style={wrap}>
                After you save your credentials, scroll down to the <b>Diagnostics</b> section on
                the same page. These buttons check that your setup works before any real customer
                tries to pay.
              </p>
              <p className="text-black light" style={wrap}>
                Click <b>Fetch Bearer Token</b>. The plugin asks the gateway for a login token using
                your Client ID and Client Secret.
              </p>
              <ul className="ml-5 list-disc">
                <li className="text-black light" style={wrap}>
                  <b>If it works</b>, a response appears below the button. Your Client ID and Client
                  Secret are correct.
                </li>
                <li className="text-black light" style={wrap}>
                  <b>If it fails</b>, an error appears. Recheck the Client ID and Client Secret, and
                  make sure they belong to the environment you selected.
                </li>
              </ul>
            </Section>
            <Section id="webhook-setup" title="Webhook Setup">
              <p className="text-black light" style={wrap}>
                The plugin uses webhooks to receive payment updates from the gateway in near real
                time. A webhook is a message the gateway sends to your store when something changes,
                for example when a payment settles, is refunded, or is disputed. These messages keep
                your Magento orders in sync with what happened at the gateway.
              </p>
              <h2 className="font-normal text-black">Getting Your Webhook URL</h2>
              <p className="text-black light" style={wrap}>
                On the plugin settings page, find the <b>Webhook URL</b> field. The plugin fills it
                in for you, and you cannot edit it. Click <b>Copy</b> to copy it. It looks like
                this:
              </p>
              <img src={screenshotWebhookUrl} alt="Screenshot of Webhook URL field" width="50%" />
              <CopyBlock
                text="https://yourstore.com/basys_iqpropayment/webhook/index"
                theme={dracula}
                customStyle={{ overflowX: 'auto' }}
                language="text"
              />
            </Section>
            <Section id="webhook-creation" title="Webhook Creation">
              <ol className="ml-5 list-decimal">
                <li className="text-black light">
                  Log in to your <b>iQ Pro+ Gateway Portal</b>.
                </li>
                <li className="text-black light">
                  Navigate to <b>Developer Hub → Webhooks</b>.
                </li>
                <li className="text-black light">
                  Click <b>Create New +</b>.
                </li>
                <li className="text-black light">
                  For <b>Webhook Type</b>, select <b>Transaction</b>.
                </li>
                <li className="text-black light" style={wrap}>
                  For <b>Webhook URL</b>, paste the URL you copied from the plugin settings page.
                </li>
                <li className="text-black light">
                  Click <b>Create</b> to save the webhook.
                  <img src={screenshotWebhooks} alt="Screenshot of Webhooks" />
                </li>
              </ol>
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>IMPORTANT:</strong> Webhooks require a live, publicly reachable site with
                  a valid and trusted SSL certificate. Local or staging sites that are not reachable
                  from the internet will not receive webhook messages.
                </p>
              </div>
              <h2 className="font-normal text-black">Make Sure Magento Cron Is Running</h2>
              <p className="text-black light" style={wrap}>
                The plugin saves each webhook message the moment it arrives, then applies it to the
                matching order on the next cron run. Magento cron runs every minute on most stores.
                If cron is not running on your server, webhook messages pile up and your orders
                never update.
              </p>
              <p className="text-black light" style={wrap}>
                To check whether cron is running, ask your developer or hosting provider, or run:
              </p>
              <CopyBlock
                text="bin/magento cron:run"
                theme={dracula}
                customStyle={{ overflowX: 'auto' }}
                language="bash"
              />
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>NOTE:</strong> Most managed Magento hosting sets cron up for you. If your
                  orders never pick up gateway updates, cron is the first thing to check.
                </p>
              </div>
              <h2 className="font-normal text-black">What Webhooks Do to Your Orders</h2>
              <table className="tg" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '35%' }} />
                  <col style={{ width: '65%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="font-bold text-center text-black">Gateway event</th>
                    <th className="font-bold text-center text-black">
                      What happens to the Magento order
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Settlement pending</td>
                    <td>A note is added to the order history.</td>
                  </tr>
                  <tr>
                    <td>Settled</td>
                    <td>A note is added with the settlement ID, batch, and amount settled.</td>
                  </tr>
                  <tr>
                    <td>Declined or error after approval</td>
                    <td>A note is added and the order is placed on hold for you to review.</td>
                  </tr>
                  <tr>
                    <td>Refunded at the gateway</td>
                    <td>
                      A credit memo is recorded in Magento so your totals match. If the whole order
                      was refunded and nothing has shipped, the order is also canceled. A partial
                      refund leaves the order open.
                    </td>
                  </tr>
                  <tr>
                    <td>Voided at the gateway</td>
                    <td>A credit memo is recorded and the order is canceled.</td>
                  </tr>
                  <tr>
                    <td>Chargeback or dispute</td>
                    <td>
                      The order is placed on hold and marked with the status “Charged Back”. No
                      money is moved in Magento, because the card issuer has already pulled the
                      funds.
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>NOTE:</strong> The plugin records webhook credit memos offline. Magento
                  writes the refund down for your records but sends nothing back to the gateway,
                  because the money already moved there.
                </p>
              </div>
            </Section>
            <Section id="turning-on-the-payment-method" title="Turning On the Payment Method">
              <p className="text-black light" style={wrap}>
                After the connection test passes and your webhook is created, switch the payment
                method on.
              </p>
              <ol className="ml-5 list-decimal">
                <li className="text-black light" style={wrap}>
                  Go to{' '}
                  <b>Stores → Configuration → Sales → Payment Methods → iQPro+ Payment Gateway</b>.
                </li>
                <li className="text-black light">
                  Set <b>Enabled</b> to <b>Yes</b>.
                </li>
                <li className="text-black light" style={wrap}>
                  Set <b>Title</b> to whatever you want shoppers to see, for example{' '}
                  <b>Credit Card</b>.
                </li>
                <li className="text-black light">
                  Click <b>Save Config</b>.
                </li>
                <li className="text-black light" style={wrap}>
                  Flush the cache so the change reaches your storefront:
                  <CopyBlock
                    text="bin/magento cache:flush"
                    theme={dracula}
                    customStyle={{ overflowX: 'auto' }}
                    language="bash"
                  />
                </li>
              </ol>
              <p className="text-black light" style={wrap}>
                You can also flush the cache from the admin at <b>System → Cache Management</b>.
              </p>
              <p className="text-black light" style={wrap}>
                Now open your storefront, add an item to the cart, and go to checkout. Your iQ Pro+
                option will appear in the payment step.
              </p>
              <h2 className="font-normal text-black">What Your Customers See</h2>
              <p className="text-black light" style={wrap}>
                At the payment step, the shopper sees a card number field, an expiration date field,
                and a security code (CVV) field. The gateway hosts the card number and security code
                fields inside secure windows. They look like normal fields, but what your shopper
                types in them goes straight to the gateway, never to your store.
              </p>
              <img
                src={screenshotCheckout}
                alt="Screenshot of iQPro+ Payment at checkout"
                width="50%"
              />
              <p className="text-black light" style={wrap}>
                When the shopper clicks <b>Place Order</b>, the gateway swaps the card details for a
                token, and your store sends only that token to take the payment. If the card is
                declined, the shopper sees the reason and can try a different card.
              </p>
            </Section>
            <Section id="managing-orders-after-payment" title="Managing Orders After Payment">
              <p className="text-black light" style={wrap}>
                The plugin charges the full amount the moment the shopper places the order. Magento
                then creates a paid invoice and moves the order to <b>Processing</b>.
              </p>
              <h2 className="font-normal text-black">Voiding a Payment</h2>
              <p className="text-black light" style={wrap}>
                A void cancels a payment before it settles. Settlement happens at the end of the
                business day for most accounts. Nothing has moved yet, so a void leaves no trace on
                the customer’s statement, and that makes it the better choice whenever it is
                available.
              </p>
              <ol className="ml-5 list-decimal">
                <li className="text-black light">
                  Go to <b>Sales → Orders</b> and open the order.
                </li>
                <li className="text-black light">
                  Click the <b>Invoices</b> tab and open the invoice.
                </li>
                <li className="text-black light">
                  Click <b>Void</b> at the top of the invoice.
                </li>
              </ol>
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>NOTE:</strong> If the payment has already settled, the plugin stops and
                  tells you to issue a refund instead. It changes nothing when this happens.
                </p>
              </div>
              <h2 className="font-normal text-black">Refunding a Payment</h2>
              <p className="text-black light" style={wrap}>
                A refund sends money back to the customer after the payment settles. It shows up as
                a separate credit on their statement in a few business days.
              </p>
              <ol className="ml-5 list-decimal">
                <li className="text-black light">
                  Go to <b>Sales → Orders</b> and open the order.
                </li>
                <li className="text-black light">
                  Click the <b>Invoices</b> tab and open the invoice.
                </li>
                <li className="text-black light">
                  Click <b>Credit Memo</b>.
                </li>
                <li className="text-black light" style={wrap}>
                  Adjust the quantities or amounts if you are only refunding part of the order.
                </li>
                <li className="text-black light">
                  Click <b>Refund</b> at the bottom of the page.
                </li>
              </ol>
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>IMPORTANT:</strong> Click <b>Refund</b>, not <b>Refund Offline</b>.
                  “Refund” sends the refund to the gateway and moves real money. “Refund Offline”
                  only writes the refund down in Magento and sends nothing to the gateway. Use
                  Refund Offline only if you already refunded the customer some other way.
                </p>
              </div>
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>NOTE:</strong> If the payment has not settled yet, the plugin stops and
                  tells you to void it instead.
                </p>
              </div>
              <h2 className="font-normal text-black">Void or Refund: Which One?</h2>
              <table className="tg" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '55%' }} />
                  <col style={{ width: '45%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="font-bold text-center text-black">Situation</th>
                    <th className="font-bold text-center text-black">Use this</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>The order was placed today and has not settled yet</td>
                    <td>Void</td>
                  </tr>
                  <tr>
                    <td>The payment has settled</td>
                    <td>Refund (credit memo)</td>
                  </tr>
                  <tr>
                    <td>You only want to give part of the money back</td>
                    <td>Refund (credit memo)</td>
                  </tr>
                  <tr>
                    <td>You are not sure</td>
                    <td>
                      Try Void first. The plugin tells you if you need a refund instead, and charges
                      nothing either way.
                    </td>
                  </tr>
                </tbody>
              </table>
            </Section>
            <Section id="logging-and-troubleshooting" title="Logging and Troubleshooting">
              <p className="text-black light" style={wrap}>
                The plugin keeps its own log files to help you and your developer track down
                problems.
              </p>
              <h2 className="font-normal text-black">Viewing the Logs</h2>
              <p className="text-black light" style={wrap}>
                On the plugin settings page, find <b>Plugin Logs</b> and click <b>View Logs</b>.
                This opens a viewer where you can read, download, or delete log files without
                needing server access.
              </p>
              <img src={screenshotLogs} alt="Screenshot of iQPro+ Logs viewer" />
              <p className="text-black light" style={wrap}>
                The plugin stores log files on the server in the <code>var/log</code> folder, named
                like this:
              </p>
              <CopyBlock
                text={`basys-iqproplus-transaction-logs-2026-08-17.log
basys-iqproplus-void-logs-2026-08-17.log
basys-iqproplus-refund-logs-2026-08-17.log
basys-iqproplus-webhook-logs-2026-08-17.log
basys-iqproplus-api-logs-2026-08-17.log`}
                theme={dracula}
                customStyle={{ overflowX: 'auto' }}
                language="text"
              />
              <p className="text-black light" style={wrap}>
                There is one file per activity per day, so you can go straight to the day and the
                activity you care about. The last one, <code>api</code>, holds everything that is
                not tied to one of the other activities, including the calls that build the card
                entry fields at checkout.
              </p>
              <h2 className="font-normal text-black">Advanced Logging</h2>
              <p className="text-black light" style={wrap}>
                Set <b>Advanced Logging</b> to <b>Yes</b> to record every step of each payment,
                void, refund, and webhook, including the full messages sent to and from the gateway.
                Even with Advanced Logging turned off, the plugin records failures and a one-line
                summary of every gateway call.
              </p>
              <div className="my-4 callout">
                <p className="mb-0 text-black light" style={wrap}>
                  <strong>IMPORTANT:</strong> The plugin hides sensitive values such as your Client
                  Secret, login tokens, and card data in every log file, whatever your logging
                  setting.
                </p>
              </div>
              <h2 className="font-normal text-black">Log Cleanup</h2>
              <p className="text-black light" style={wrap}>
                Once a day, the plugin deletes log files older than the number of days in{' '}
                <b>Log Retention (Days)</b>. The default is 30 days. This runs on Magento cron, so
                cron needs to be running.
              </p>
              <h2 className="font-normal text-black">Common Problems</h2>
              <table className="tg" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '35%' }} />
                  <col style={{ width: '65%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="font-bold text-center text-black">Problem</th>
                    <th className="font-bold text-center text-black">What to check</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>iQ Pro+ does not appear at checkout</td>
                    <td>
                      Confirm Enabled is set to Yes and saved. Run the connection test in the
                      Diagnostics section. Flush the cache. Check the api log for a failed gateway
                      call.
                    </td>
                  </tr>
                  <tr>
                    <td>The card fields do not load, or show an error</td>
                    <td>
                      Your Gateway ID may be wrong or the gateway may not recognize your store
                      address. Also confirm your site loads over https. The api log has the details.
                    </td>
                  </tr>
                  <tr>
                    <td>Payments are declined</td>
                    <td>
                      Open the transaction log for that day. The decline reason from the card issuer
                      is recorded there.
                    </td>
                  </tr>
                  <tr>
                    <td>Orders never update after payment</td>
                    <td>
                      Confirm the webhook is created in the gateway portal with the correct URL, and
                      confirm Magento cron is running. Check the webhook log.
                    </td>
                  </tr>
                  <tr>
                    <td>Void or refund is rejected</td>
                    <td>
                      The plugin checks with the gateway first. If a payment has settled it must be
                      refunded, and if it has not settled it must be voided. The message tells you
                      which.
                    </td>
                  </tr>
                  <tr>
                    <td>Changes do not show up on the storefront</td>
                    <td>
                      Flush the cache. After a plugin update, also run <code>setup:upgrade</code>{' '}
                      and <code>setup:static-content:deploy</code>.
                    </td>
                  </tr>
                </tbody>
              </table>
            </Section>
            <Section id="styling" title="Styling">
              <p className="text-black light" style={wrap}>
                The plugin ships with minimal styling, so it picks up your theme’s look. It uses
                your store’s existing checkout spacing, fonts, and colors wherever it can.
              </p>
              <p className="text-black light" style={wrap}>
                The gateway hosts the card number and security code fields inside secure windows.
                They match standard Magento input fields, but because the gateway owns them, your
                developer cannot restyle them the way they would an ordinary field.
              </p>
              <p className="text-black light" style={wrap}>
                If you need to adjust the appearance, your developer can edit this file:
              </p>
              <CopyBlock
                text="app/code/Basys/IQProPayment/view/frontend/web/css/iqpro.css"
                theme={dracula}
                customStyle={{ overflowX: 'auto' }}
                language="text"
              />
              <p className="text-black light" style={wrap}>
                After changing any file, run <code>setup:static-content:deploy -f en_US</code> and{' '}
                <code>cache:flush</code> so your storefront picks up the change.
              </p>
            </Section>
            <Section id="support" title="Support">
              <p className="text-black light" style={wrap}>
                If you get stuck, contact your Basys representative. Having the following ready will
                get you an answer faster:
              </p>
              <ul className="ml-5 list-disc">
                <li className="text-black light">Your store URL.</li>
                <li className="text-black light">Whether you are in Sandbox or Production.</li>
                <li className="text-black light" style={wrap}>
                  The order number, if the problem is with a specific order.
                </li>
                <li className="text-black light" style={wrap}>
                  The relevant log file, downloaded from the plugin’s log viewer.
                </li>
              </ul>
            </Section>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default DeveloperAdobeMagento;
